import React, { useState, useEffect, useCallback } from 'react';
import {
  PlusIcon,
  UserGroupIcon,
  CalendarIcon,
  ChartBarIcon,
  UsersIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ClockIcon,
  AcademicCapIcon,
  PencilIcon,
  CheckIcon,
  UserPlusIcon
} from '@heroicons/react/24/outline';
import apiService from '../services/api';

const BatchManagement = () => {
  const [showCreateBatch, setShowCreateBatch] = useState(false);
  const [showManageStudents, setShowManageStudents] = useState(false);
  const [showScheduleEdit, setShowScheduleEdit] = useState(false);
  const [showBatchDetails, setShowBatchDetails] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchDetails, setBatchDetails] = useState(null);
  const [batchStudents, setBatchStudents] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [enrollingStudentId, setEnrollingStudentId] = useState(null);
  const [enrolledStudentIds, setEnrolledStudentIds] = useState(() => new Set());
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [batches, setBatches] = useState([]);
  const [courses, setCourses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [searchStudents, setSearchStudents] = useState('');

  const [newBatch, setNewBatch] = useState({
    name: '',
    course: '',
    teacher: '',
    start_date: '',
    end_date: '',
    max_students: '',
    schedule_days: [],
    schedule_time: '',
    description: ''
  });

  const [editScheduleDays, setEditScheduleDays] = useState([]);
  const [editScheduleTime, setEditScheduleTime] = useState('');

  useEffect(() => {
    setIsLoaded(true);
    loadBatchData();
  }, []);

  const refreshEnrolledStudents = useCallback(async (batchId) => {
    if (!batchId) {
      setEnrolledStudentIds(new Set());
      return;
    }

    try {
      const response = await apiService.getBatchStudents(batchId);
      const ids = new Set();

      if (Array.isArray(response)) {
        response.forEach((item) => {
          if (!item) {
            return;
          }

          const rawId =
            typeof item.student === 'number' || typeof item.student === 'string'
              ? item.student
              : item.student?.id ?? item.student_detail?.id ?? null;

          if (rawId !== null && rawId !== undefined) {
            const resolvedId = typeof rawId === 'string' ? parseInt(rawId, 10) || rawId : rawId;
            ids.add(resolvedId);
          }
        });
      }

      setEnrolledStudentIds(ids);
    } catch (fetchError) {
      console.error('Failed to fetch batch students:', fetchError);
    }
  }, []);

  useEffect(() => {
    if (!showManageStudents || !selectedBatch) {
      return;
    }

    setEnrollingStudentId(null);
    setEnrolledStudentIds(new Set());
    refreshEnrolledStudents(selectedBatch.id);
  }, [showManageStudents, selectedBatch, refreshEnrolledStudents]);

  const loadBatchData = async () => {
    try {
      setLoading(true);
      const [batchesData, coursesData, teachersData, studentsData] = await Promise.all([
        apiService.getBatches().catch(err => { console.error('Failed to load batches:', err); return []; }),
        apiService.getCourses().catch(err => { console.error('Failed to load courses:', err); return []; }),
        apiService.getTeachers().catch(err => { console.error('Failed to load teachers:', err); return []; }),
        apiService.getStudents().catch(err => { console.error('Failed to load students:', err); return []; })
      ]);

      const normalizedBatches = Array.isArray(batchesData) ? batchesData : [];
      setBatches(normalizedBatches);
      setCourses(Array.isArray(coursesData) ? coursesData : []);
      setTeachers(Array.isArray(teachersData) ? teachersData : []);
      setStudents(Array.isArray(studentsData) ? studentsData : []);

      if (selectedBatch) {
        const updatedSelection = normalizedBatches.find(batch => batch.id === selectedBatch.id);
        if (updatedSelection) {
          setSelectedBatch(updatedSelection);
        }
      }

      setError('');
    } catch (error) {
      console.error('Failed to load batch data:', error);
      setError('Failed to load batch data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBatch = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const batchData = {
        ...newBatch,
        schedule_days: newBatch.schedule_days.join(',') // Convert array to comma-separated string
      };
      await apiService.createBatch(batchData);
      await loadBatchData();
      setNewBatch({
        name: '',
        course: '',
        teacher: '',
        start_date: '',
        end_date: '',
        max_students: '',
        schedule_days: [],
        schedule_time: '',
        description: ''
      });
      setShowCreateBatch(false);
      setError('');
    } catch (error) {
      console.error('Failed to create batch:', error);
      setError('Failed to create batch. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEnrollStudent = async (studentId) => {
    if (!selectedBatch) {
      return;
    }

    try {
      setEnrollingStudentId(studentId);
      setError('');
      setSuccess('');

      await apiService.enrollStudent(selectedBatch.id, studentId);
      await loadBatchData();
      await refreshEnrolledStudents(selectedBatch.id);

      setEnrolledStudentIds((prev) => {
        const updated = new Set(prev);
        updated.add(studentId);
        return updated;
      });

      setSuccess('Student enrolled successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Failed to enroll student:', error);

      const errorMessage =
        error?.detail || error?.message || (error?.response?.data && Object.values(error.response.data)[0]) || null;

      setError(
        typeof errorMessage === 'string'
          ? errorMessage
          : 'Failed to enroll student. Please try again.'
      );
    } finally {
      setEnrollingStudentId(null);
    }
  };

  const handleUpdateSchedule = async () => {
    try {
      setLoading(true);
      const scheduleData = {
        schedule_days: editScheduleDays.join(','), // Convert array to comma-separated string
        schedule_time: editScheduleTime
      };
      await apiService.updateBatch(selectedBatch.id, scheduleData);
      await loadBatchData();
      setShowScheduleEdit(false);
      setSelectedBatch(null);
      setSuccess('Schedule updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
      setError('');
    } catch (error) {
      console.error('Failed to update schedule:', error);
      setError('Failed to update schedule. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleShowBatchDetails = async (batch) => {
    try {
      setLoading(true);
      setSelectedBatch(batch);
      
      const [details, students] = await Promise.all([
        apiService.getBatchDetails(batch.id),
        apiService.getBatchStudents(batch.id)
      ]);
      
      setBatchDetails(details);
      setBatchStudents(students);
      setShowBatchDetails(true);
      setError('');
    } catch (error) {
      console.error('Failed to load batch details:', error);
      setError('Failed to load batch details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get color gradients
  const getColorGradient = (index) => {
    const gradients = [
      'from-blue-500 via-indigo-500 to-purple-600',
      'from-green-500 via-emerald-500 to-teal-600',
      'from-red-500 via-pink-500 to-rose-600',
      'from-purple-500 via-violet-500 to-indigo-600',
      'from-yellow-500 via-orange-500 to-red-600',
      'from-cyan-500 via-blue-500 to-indigo-600'
    ];
    return gradients[index % gradients.length];
  };

  // Helper function to determine batch status based on dates
  const getBatchStatus = (startDate, endDate) => {
    const today = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    today.setHours(0, 0, 0, 0);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    
    if (today < start) return 'Upcoming';
    if (today > end) return 'Completed';
    return 'Active';
  };

  // Helper function to calculate time progress
  const getTimeProgress = (startDate, endDate) => {
    const today = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (today < start) return 0;
    if (today > end) return 100;
    
    const totalDuration = end - start;
    const elapsed = today - start;
    return Math.round((elapsed / totalDuration) * 100);
  };

  // Helper function to get days remaining
  const getDaysRemaining = (endDate) => {
    const today = new Date();
    const end = new Date(endDate);
    const diffTime = end - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  // Process batches data with enhanced information
  const processedBatches = batches.map((batch, index) => {
    // Use course_name from backend instead of looking up manually
    const courseName = batch.course_name || 'Unknown Course';
    
    // Use students_count from backend (which maps to current_student_count)
    const studentCount = batch.students_count || batch.current_student_count || 0;
    
    // Use teacher_name from backend directly
    const teacherName = batch.teacher_name || 'Not Assigned';
    const status = getBatchStatus(batch.start_date, batch.end_date);
    const timeProgress = getTimeProgress(batch.start_date, batch.end_date);
    const daysRemaining = getDaysRemaining(batch.end_date);
    
    return {
      ...batch,
      courseName: courseName,
      teacherName: teacherName,
      students: studentCount,
      maxStudents: batch.max_students || 30,
      status,
      timeProgress,
      daysRemaining,
      completion: batch.completion_percentage || timeProgress,
      schedule: batch.schedule || 'Schedule TBD', // Use backend computed schedule
      color: getColorGradient(index)
    };
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200/60';
      case 'Upcoming':
        return 'bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 border border-blue-200/60';
      case 'Completed':
        return 'bg-gradient-to-r from-gray-100 to-slate-100 text-gray-800 border border-gray-200/60';
      default:
        return 'bg-gradient-to-r from-gray-100 to-slate-100 text-gray-800 border border-gray-200/60';
    }
  };

  // Calculate stats
  const stats = {
    active: processedBatches.filter(b => b.status === 'Active').length,
    upcoming: processedBatches.filter(b => b.status === 'Upcoming').length,
    totalStudents: processedBatches.reduce((sum, batch) => sum + batch.students, 0),
    avgCompletion: processedBatches.length > 0 ? 
      Math.round(processedBatches.reduce((sum, batch) => sum + batch.completion, 0) / processedBatches.length) : 0
  };

  // Filter students for search
  const filteredStudents = students.filter(student =>
    student.full_name?.toLowerCase().includes(searchStudents.toLowerCase()) ||
    student.email?.toLowerCase().includes(searchStudents.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">Batch Management</h1>
          <p className="mt-2 text-gray-600 font-medium">
            Create and manage student batches across different courses
          </p>
        </div>
        <button 
          onClick={() => setShowCreateBatch(true)}
          className="mt-6 sm:mt-0 btn-primary group"
        >
          <PlusIcon className="h-5 w-5 mr-2 group-hover:scale-110 transition-transform duration-200" />
          Create New Batch
        </button>
      </div>

      {/* Success Message */}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-800">{success}</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="stat-card group">
          <div className="flex items-center">
            <div className="p-3 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 shadow-lg group-hover:shadow-xl group-hover:shadow-blue-500/25 transition-all duration-300">
              <UserGroupIcon className="h-5 w-5 text-white group-hover:scale-110 transition-transform duration-300" />
            </div>
            <div className="ml-3">
              <p className="text-xs font-semibold text-gray-600">Active Batches</p>
              <p className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                {loading ? '...' : stats.active}
              </p>
            </div>
          </div>
        </div>
        <div className="stat-card group">
          <div className="flex items-center">
            <div className="p-3 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 shadow-lg group-hover:shadow-xl group-hover:shadow-green-500/25 transition-all duration-300">
              <UsersIcon className="h-5 w-5 text-white group-hover:scale-110 transition-transform duration-300" />
            </div>
            <div className="ml-3">
              <p className="text-xs font-semibold text-gray-600">Total Students</p>
              <p className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                {loading ? '...' : stats.totalStudents}
              </p>
            </div>
          </div>
        </div>
        <div className="stat-card group">
          <div className="flex items-center">
            <div className="p-3 rounded-xl bg-gradient-to-r from-purple-500 to-violet-600 shadow-lg group-hover:shadow-xl group-hover:shadow-purple-500/25 transition-all duration-300">
              <CalendarIcon className="h-5 w-5 text-white group-hover:scale-110 transition-transform duration-300" />
            </div>
            <div className="ml-3">
              <p className="text-xs font-semibold text-gray-600">Upcoming Batches</p>
              <p className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                {loading ? '...' : stats.upcoming}
              </p>
            </div>
          </div>
        </div>
        <div className="stat-card group">
          <div className="flex items-center">
            <div className="p-3 rounded-xl bg-gradient-to-r from-orange-500 to-red-600 shadow-lg group-hover:shadow-xl group-hover:shadow-orange-500/25 transition-all duration-300">
              <ChartBarIcon className="h-5 w-5 text-white group-hover:scale-110 transition-transform duration-300" />
            </div>
            <div className="ml-3">
              <p className="text-xs font-semibold text-gray-600">Avg. Progress</p>
              <p className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                {loading ? '...' : stats.avgCompletion}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Compact Batches Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {loading ? (
          <div className="col-span-full text-center py-8">
            <div className="text-gray-500">Loading batches...</div>
          </div>
        ) : error ? (
          <div className="col-span-full text-center py-8">
            <div className="text-red-500">{error}</div>
          </div>
        ) : processedBatches.length === 0 ? (
          <div className="col-span-full text-center py-8">
            <div className="text-gray-500">No batches created yet. Create your first batch to get started!</div>
          </div>
        ) : (
          processedBatches.map((batch, index) => (
          <div 
            key={batch.id} 
            className={`bg-white/80 backdrop-blur-xl border border-white/20 rounded-2xl p-5 shadow-xl shadow-black/5 hover:shadow-2xl hover:shadow-black/10 transition-all duration-500 ease-out group hover:scale-[1.02] relative overflow-hidden ${isLoaded ? 'animate-in fade-in slide-in-from-bottom-4' : 'opacity-0'}`}
            style={{ animationDelay: `${index * 100}ms` }}
          >
            {/* Status badge */}
            <div className="absolute top-4 right-4">
              <span className={`px-3 py-1 text-xs font-bold rounded-full ${getStatusColor(batch.status)}`}>
                {batch.status}
              </span>
            </div>

            <div className="relative z-10">
              {/* Header */}
              <div className="mb-4">
                <div className="flex items-center mb-2">
                  <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${batch.color} mr-2 shadow-lg animate-pulse`}></div>
                  <h3 className="text-lg font-bold text-gray-900 truncate pr-16">
                    {batch.name}
                  </h3>
                </div>
                <p className="text-gray-600 font-medium text-sm flex items-center">
                  <AcademicCapIcon className="h-4 w-4 mr-1" />
                  {batch.courseName}
                </p>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="text-center p-3 bg-gradient-to-br from-gray-50/80 to-white/60 backdrop-blur-sm rounded-xl border border-gray-200/40">
                  <p className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                    {batch.students}/{batch.maxStudents}
                  </p>
                  <p className="text-xs font-semibold text-gray-600">Students</p>
                </div>
                <div className="text-center p-3 bg-gradient-to-br from-gray-50/80 to-white/60 backdrop-blur-sm rounded-xl border border-gray-200/40">
                  <p className="text-xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent flex items-center justify-center">
                    <ClockIcon className="h-4 w-4 mr-1" />
                    {batch.daysRemaining}d
                  </p>
                  <p className="text-xs font-semibold text-gray-600">Remaining</p>
                </div>
              </div>

              {/* Teacher & Schedule */}
              <div className="space-y-2 mb-4">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600 font-medium">Teacher:</span>
                  <span className="font-bold text-gray-900 truncate">{batch.teacherName}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600 font-medium">Schedule:</span>
                  <span className="font-bold text-gray-900 truncate">{batch.schedule || 'TBD'}</span>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-700 font-semibold text-sm">Time Progress</span>
                  <span className="font-bold text-sm bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                    {batch.timeProgress}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 shadow-inner">
                  <div 
                    className={`h-2 rounded-full bg-gradient-to-r ${batch.color} shadow-sm transition-all duration-1000 ease-out`}
                    style={{ width: `${batch.timeProgress}%` }}
                  ></div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-3 gap-2">
                <button 
                  onClick={() => {
                    setSelectedBatch(batch);
                    setShowManageStudents(true);
                  }}
                  className="text-xs bg-white/80 backdrop-blur-sm border border-gray-200/60 text-gray-700 hover:text-indigo-600 hover:border-indigo-300 hover:bg-indigo-50/80 font-semibold py-2 px-2 rounded-xl transition-all duration-300 group/btn flex items-center justify-center"
                >
                  <UserPlusIcon className="h-3 w-3 mr-1 group-hover/btn:scale-110 transition-transform duration-200" />
                  Students
                </button>
                <button 
                  onClick={() => {
                    setSelectedBatch(batch);
                    // Parse existing schedule properly
                    const days = batch.schedule_days ? batch.schedule_days.split(',').map(d => d.trim()) : [];
                    setEditScheduleDays(days);
                    setEditScheduleTime(batch.schedule_time || '');
                    setShowScheduleEdit(true);
                  }}
                  className="text-xs bg-white/80 backdrop-blur-sm border border-gray-200/60 text-gray-700 hover:text-purple-600 hover:border-purple-300 hover:bg-purple-50/80 font-semibold py-2 px-2 rounded-xl transition-all duration-300 group/btn flex items-center justify-center"
                >
                  <PencilIcon className="h-3 w-3 mr-1 group-hover/btn:scale-110 transition-transform duration-200" />
                  Schedule
                </button>
                <button 
                  onClick={() => handleShowBatchDetails(batch)}
                  className="text-xs bg-white/80 backdrop-blur-sm border border-gray-200/60 text-gray-700 hover:text-green-600 hover:border-green-300 hover:bg-green-50/80 font-semibold py-2 px-2 rounded-xl transition-all duration-300 group/btn flex items-center justify-center"
                >
                  <ChartBarIcon className="h-3 w-3 mr-1 group-hover/btn:scale-110 transition-transform duration-200" />
                  Details
                </button>
              </div>
            </div>
          </div>
          ))
        )}
      </div>

      {/* Create Batch Modal */}
      {showCreateBatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Create New Batch</h3>
              <button
                onClick={() => setShowCreateBatch(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
            
            <form onSubmit={handleCreateBatch} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Batch Name</label>
                  <input 
                    type="text" 
                    className="input-field" 
                    placeholder="e.g., Data Science Batch 3"
                    value={newBatch.name}
                    onChange={(e) => setNewBatch({...newBatch, name: e.target.value})}
                    required
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Course</label>
                  <select 
                    className="input-field"
                    value={newBatch.course}
                    onChange={(e) => setNewBatch({...newBatch, course: e.target.value})}
                    required
                    disabled={loading}
                  >
                    <option value="">Select Course</option>
                    {courses.map((course) => (
                      <option key={course.id} value={course.id}>{course.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input 
                    type="date" 
                    className="input-field"
                    value={newBatch.start_date}
                    onChange={(e) => setNewBatch({...newBatch, start_date: e.target.value})}
                    required
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input 
                    type="date" 
                    className="input-field"
                    value={newBatch.end_date}
                    onChange={(e) => setNewBatch({...newBatch, end_date: e.target.value})}
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Teacher</label>
                  <select 
                    className="input-field"
                    value={newBatch.teacher}
                    onChange={(e) => setNewBatch({...newBatch, teacher: e.target.value})}
                    required
                    disabled={loading}
                  >
                    <option value="">Select Teacher</option>
                    {teachers.map((teacher) => (
                      <option key={teacher.id} value={teacher.id}>{teacher.full_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Students</label>
                  <input 
                    type="number" 
                    className="input-field" 
                    placeholder="30"
                    value={newBatch.max_students}
                    onChange={(e) => setNewBatch({...newBatch, max_students: e.target.value})}
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Schedule</label>
                
                {/* Days Selection */}
                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-600 mb-2">Select Days (Choose 3 days)</label>
                  <div className="grid grid-cols-7 gap-2">
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                      <label key={day} className="flex flex-col items-center">
                        <input
                          type="checkbox"
                          className="mb-1"
                          checked={newBatch.schedule_days.includes(day)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              if (newBatch.schedule_days.length < 3) {
                                setNewBatch({
                                  ...newBatch,
                                  schedule_days: [...newBatch.schedule_days, day]
                                });
                              }
                            } else {
                              setNewBatch({
                                ...newBatch,
                                schedule_days: newBatch.schedule_days.filter(d => d !== day)
                              });
                            }
                          }}
                          disabled={loading || (!newBatch.schedule_days.includes(day) && newBatch.schedule_days.length >= 3)}
                        />
                        <span className="text-xs text-gray-600">{day.slice(0, 3)}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Time Input */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Time Slot</label>
                  <input 
                    type="text" 
                    className="input-field" 
                    placeholder="e.g., 1:00 PM - 3:00 PM"
                    value={newBatch.schedule_time}
                    onChange={(e) => setNewBatch({...newBatch, schedule_time: e.target.value})}
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea 
                  className="input-field" 
                  rows="3" 
                  placeholder="Batch description..."
                  value={newBatch.description}
                  onChange={(e) => setNewBatch({...newBatch, description: e.target.value})}
                  disabled={loading}
                />
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button 
                  type="submit" 
                  className="flex-1 btn-primary disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? 'Creating...' : 'Create Batch'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateBatch(false)}
                  className="flex-1 btn-secondary"
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manage Students Modal */}
      {showManageStudents && selectedBatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Manage Students - {selectedBatch.name}
              </h3>
              <button
                onClick={() => {
                  setShowManageStudents(false);
                  setSelectedBatch(null);
                  setSearchStudents('');
                  setEnrollingStudentId(null);
                  setEnrolledStudentIds(new Set());
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-4">
              <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search students by name or email..."
                  className="pl-10 input-field"
                  value={searchStudents}
                  onChange={(e) => setSearchStudents(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {filteredStudents.map((student) => {
                const isEnrolling = enrollingStudentId === student.id;
                const isEnrolled = enrolledStudentIds.has(student.id);

                return (
                  <div key={student.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <h4 className="font-semibold text-gray-900">{student.full_name}</h4>
                      <p className="text-gray-600 text-sm">{student.email}</p>
                    </div>
                    <button
                      onClick={() => handleEnrollStudent(student.id)}
                      className={`btn-primary flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed ${
                        isEnrolled ? 'bg-green-100 text-green-700 border border-green-200 hover:bg-green-100' : ''
                      }`}
                      disabled={isEnrolling || isEnrolled}
                    >
                      {isEnrolled ? (
                        <>
                          <CheckIcon className="h-4 w-4" />
                          Enrolled
                        </>
                      ) : isEnrolling ? (
                        'Enrolling...'
                      ) : (
                        'Enroll'
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Schedule Edit Modal */}
      {showScheduleEdit && selectedBatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Edit Schedule - {selectedBatch.name}
              </h3>
              <button
                onClick={() => {
                  setShowScheduleEdit(false);
                  setSelectedBatch(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Current Schedule</label>
                <div className="p-3 bg-gray-50 rounded-lg border">
                  <span className="text-gray-900 font-medium">
                    {selectedBatch.schedule || 'No schedule set'}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Update Schedule</label>
                
                {/* Days Selection */}
                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-600 mb-2">Select Days (Choose up to 3 days)</label>
                  <div className="grid grid-cols-7 gap-2">
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                      <label key={day} className="flex flex-col items-center">
                        <input
                          type="checkbox"
                          className="mb-1"
                          checked={editScheduleDays.includes(day)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              if (editScheduleDays.length < 3) {
                                setEditScheduleDays([...editScheduleDays, day]);
                              }
                            } else {
                              setEditScheduleDays(editScheduleDays.filter(d => d !== day));
                            }
                          }}
                          disabled={loading || (!editScheduleDays.includes(day) && editScheduleDays.length >= 3)}
                        />
                        <span className="text-xs text-gray-600">{day.slice(0, 3)}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Time Input */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Time Slot</label>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="e.g., 1:00 PM - 3:00 PM"
                    value={editScheduleTime}
                    onChange={(e) => setEditScheduleTime(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={handleUpdateSchedule}
                  className="flex-1 btn-primary"
                  disabled={loading}
                >
                  <CheckIcon className="h-4 w-4 mr-2" />
                  {loading ? 'Updating...' : 'Update Schedule'}
                </button>
                <button
                  onClick={() => {
                    setShowScheduleEdit(false);
                    setSelectedBatch(null);
                  }}
                  className="flex-1 btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Batch Details Modal */}
      {showBatchDetails && selectedBatch && batchDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">
                Batch Details - {selectedBatch.name}
              </h3>
              <button
                onClick={() => {
                  setShowBatchDetails(false);
                  setSelectedBatch(null);
                  setBatchDetails(null);
                  setBatchStudents([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-5 rounded-2xl border border-blue-200/40">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <AcademicCapIcon className="h-5 w-5 mr-2 text-blue-600" />
                    Basic Information
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600 font-medium">Course:</span>
                      <span className="font-bold text-gray-900">{selectedBatch.courseName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 font-medium">Teacher:</span>
                      <span className="font-bold text-gray-900">{batchDetails.teacher_name || selectedBatch.teacherName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 font-medium">Status:</span>
                      <span className={`px-3 py-1 text-xs font-bold rounded-full ${getStatusColor(selectedBatch.status)}`}>
                        {selectedBatch.status}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 font-medium">Schedule:</span>
                      <span className="font-bold text-gray-900">{selectedBatch.schedule || 'Not set'}</span>
                    </div>
                  </div>
                </div>

                {/* Timeline */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-5 rounded-2xl border border-green-200/40">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <CalendarIcon className="h-5 w-5 mr-2 text-green-600" />
                    Timeline & Progress
                  </h4>
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-gray-600 font-medium">Start Date:</span>
                      <span className="font-bold text-gray-900">{selectedBatch.start_date}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 font-medium">End Date:</span>
                      <span className="font-bold text-gray-900">{selectedBatch.end_date}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 font-medium">Days Remaining:</span>
                      <span className="font-bold text-blue-600 flex items-center">
                        <ClockIcon className="h-4 w-4 mr-1" />
                        {selectedBatch.daysRemaining} days
                      </span>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-700 font-semibold">Time Progress</span>
                        <span className="font-bold text-lg bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                          {selectedBatch.timeProgress}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3 shadow-inner">
                        <div 
                          className={`h-3 rounded-full bg-gradient-to-r ${selectedBatch.color} shadow-sm transition-all duration-1000 ease-out`}
                          style={{ width: `${selectedBatch.timeProgress}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Students & Capacity */}
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-5 rounded-2xl border border-purple-200/40">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <UsersIcon className="h-5 w-5 mr-2 text-purple-600" />
                    Student Information
                  </h4>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center p-4 bg-white/60 rounded-xl">
                        <p className="text-2xl font-bold text-purple-600">{selectedBatch.students}</p>
                        <p className="text-sm font-semibold text-gray-600">Enrolled</p>
                      </div>
                      <div className="text-center p-4 bg-white/60 rounded-xl">
                        <p className="text-2xl font-bold text-indigo-600">{selectedBatch.maxStudents}</p>
                        <p className="text-sm font-semibold text-gray-600">Capacity</p>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-700 font-semibold">Capacity</span>
                        <span className="font-bold text-lg text-purple-600">
                          {Math.round((selectedBatch.students / selectedBatch.maxStudents) * 100)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3 shadow-inner">
                        <div 
                          className="h-3 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 shadow-sm transition-all duration-1000 ease-out"
                          style={{ width: `${(selectedBatch.students / selectedBatch.maxStudents) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Enrolled Students List */}
                <div className="bg-gradient-to-br from-orange-50 to-yellow-50 p-5 rounded-2xl border border-orange-200/40">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <UserGroupIcon className="h-5 w-5 mr-2 text-orange-600" />
                    Enrolled Students ({batchStudents.length})
                  </h4>
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {batchStudents.length > 0 ? (
                      batchStudents.map((studentBatch) => (
                        <div key={studentBatch.id} className="flex items-center justify-between p-3 bg-white/60 rounded-lg">
                          <div>
                            <h5 className="font-semibold text-gray-900">
                              {studentBatch.student_detail?.full_name || studentBatch.student_name || 'Unknown Student'}
                            </h5>
                            <p className="text-gray-600 text-sm">
                              {studentBatch.student_detail?.email || 'No email'}
                            </p>
                          </div>
                          <div className="text-right">
                            <span className={`px-2 py-1 text-xs font-bold rounded-full ${
                              studentBatch.status === 'active' ? 'bg-green-100 text-green-800' :
                              studentBatch.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {studentBatch.status}
                            </span>
                            <p className="text-xs text-gray-500 mt-1">
                              Enrolled: {new Date(studentBatch.enrolled_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 text-center py-4">No students enrolled yet</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3 mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowBatchDetails(false);
                  setSelectedBatch(selectedBatch);
                  setShowManageStudents(true);
                  setBatchDetails(null);
                  setBatchStudents([]);
                }}
                className="btn-primary flex items-center"
              >
                <UserPlusIcon className="h-4 w-4 mr-2" />
                Manage Students
              </button>
              <button
                onClick={() => {
                  setShowBatchDetails(false);
                  setBatchDetails(null);
                  setBatchStudents([]);
                  // Parse existing schedule properly
                  const days = selectedBatch.schedule_days ? selectedBatch.schedule_days.split(',').map(d => d.trim()) : [];
                  setEditScheduleDays(days);
                  setEditScheduleTime(selectedBatch.schedule_time || '');
                  setShowScheduleEdit(true);
                }}
                className="btn-secondary flex items-center"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                Edit Schedule
              </button>
              <button
                onClick={() => {
                  setShowBatchDetails(false);
                  setSelectedBatch(null);
                  setBatchDetails(null);
                  setBatchStudents([]);
                }}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchManagement; 